import { useState } from 'react';
import { Shell } from '../../components/layout/Shell.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { tr, trf } from '../../lib/i18n';
import { nomDuChantier } from '../../lib/nom-chantier';
import { C } from '../../theme/palette';

/**
 * Les offres « sans les prix » (Réglages → Accès) : le technicien ou le
 * directeur des travaux voit les travaux à faire, les articles et les
 * quantités, et ce qui a été ajouté par avenant. Aucun prix, aucun total ; il
 * ne modifie pas l'offre (le serveur le refuse aussi).
 */

/** « 210 m² », « 1 forfait ». */
function quantite(l) {
  const q = Number(l?.q ?? 0);
  const texte = Number.isInteger(q) ? String(q) : String(Math.round(q * 100) / 100);
  return [texte, l?.u].filter(Boolean).join(' ');
}

function Ligne({ l }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 10,
        padding: '8px 0',
        borderBottom: '1px solid ' + C.border,
        fontSize: 13.5,
      }}
    >
      <span style={{ minWidth: 0 }}>
        {l.d}
        {l.sd ? (
          <span style={{ display: 'block', fontSize: 12, color: C.grey }}>{l.sd}</span>
        ) : null}
      </span>
      <span style={{ fontWeight: 800, whiteSpace: 'nowrap' }}>{quantite(l)}</span>
    </div>
  );
}

const STATUT_COULEUR = { Accepté: '#136B3F', Refusé: '#9B2C24' };

/**
 * @param {object} props
 * @param {object[]} props.devis les offres (déjà limitées par le serveur)
 * @param {object[]} props.clients les clients
 * @param {object[]} props.chantiers les chantiers, avec leurs avenants
 */
export function OffresSansPrix({
  devis = [],
  clients = [],
  chantiers = [],
  onNav,
  soc,
  estMixte,
  univers,
  setUnivers,
}) {
  const [ouverte, setOuverte] = useState(null);
  const chantierDe = (d) =>
    (chantiers || []).find((c) => c.id === d.siteId || (d.id != null && c.devisId === d.id));
  const client = (d) => (clients || []).find((c) => c.id === d.clientId)?.nom || '';
  const d = ouverte != null ? devis.find((x) => x.id === ouverte) : null;

  if (d) {
    const ch = chantierDe(d);
    const lignes = (d.lignes || []).filter((l) => String(l.d || '').trim());
    const avenants = (ch?.avenants || []).filter((a) => (a.lignes || []).length || a.motif);
    return (
      <Shell
        active="devis"
        onNav={onNav}
        soc={soc}
        back
        onBack={() => setOuverte(null)}
        pageTitle={trf('📄 Offre {0}', d.num || '')}
        estMixte={estMixte}
        univers={univers}
        setUnivers={setUnivers}
      >
        <div style={{ fontSize: 12.5, color: C.grey, lineHeight: 1.45, marginBottom: 10 }}>
          {tr('Les travaux à faire et les quantités. Les prix restent au bureau.')}
        </div>
        <Card style={{ padding: 12, marginBottom: 12 }}>
          <div style={{ fontSize: 15, fontWeight: 800 }}>{client(d)}</div>
          {ch && (
            <div style={{ fontSize: 13, color: C.grey }}>🏗 {nomDuChantier(ch, chantiers)}</div>
          )}
          {d.desc && <div style={{ fontSize: 13, marginTop: 6 }}>{d.desc}</div>}
        </Card>
        <Card style={{ padding: 12, marginBottom: 12 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 800,
              color: C.grey,
              letterSpacing: 0.5,
              marginBottom: 4,
            }}
          >
            {tr('LES TRAVAUX À FAIRE')}
          </div>
          {lignes.length === 0 ? (
            <div style={{ fontSize: 13, color: C.grey }}>{tr('Aucune ligne.')}</div>
          ) : (
            lignes.map((l, i) => <Ligne key={l.id ?? i} l={l} />)
          )}
        </Card>
        {avenants.length > 0 && (
          <Card style={{ padding: 12, marginBottom: 12 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 800,
                color: C.grey,
                letterSpacing: 0.5,
                marginBottom: 4,
              }}
            >
              {tr("EN PLUS DE L'OFFRE (AVENANTS)")}
            </div>
            {avenants.map((a, i) => (
              <div key={a.id ?? i} style={{ marginBottom: 8 }}>
                {a.motif && (
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.purple }}>➕ {a.motif}</div>
                )}
                {(a.lignes || []).map((l, j) => (
                  <Ligne key={l.id ?? j} l={l} />
                ))}
                {a.sig ? (
                  <div style={{ fontSize: 12, color: C.grey, marginTop: 2 }}>
                    {a.date ? trf('Signé par le client le {0}', a.date) : tr('Signé par le client')}
                  </div>
                ) : null}
              </div>
            ))}
          </Card>
        )}
      </Shell>
    );
  }

  return (
    <Shell
      active="devis"
      onNav={onNav}
      soc={soc}
      pageTitle={tr('📄 Offres — mes chantiers')}
      estMixte={estMixte}
      univers={univers}
      setUnivers={setUnivers}
    >
      <div style={{ fontSize: 12.5, color: C.grey, lineHeight: 1.45, marginBottom: 10 }}>
        {tr(
          'Les travaux et les quantités de chaque offre, pour suivre le chantier. Sans les prix.',
        )}
      </div>
      {devis.length === 0 && (
        <div style={{ fontSize: 13, color: C.grey }}>{tr('Aucune offre pour le moment.')}</div>
      )}
      {devis.map((x) => {
        const ch = chantierDe(x);
        return (
          <button
            key={x.id}
            type="button"
            onClick={() => setOuverte(x.id)}
            style={{
              width: '100%',
              textAlign: 'left',
              font: 'inherit',
              cursor: 'pointer',
              background: C.white,
              border: '1px solid ' + C.border,
              borderRadius: 14,
              padding: 12,
              marginBottom: 10,
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <b style={{ fontSize: 15, color: C.ink }}>
                {x.num} · {client(x)}
              </b>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: STATUT_COULEUR[x.statut] || '#8A5A00',
                }}
              >
                {tr(x.statut || '')}
              </span>
            </span>
            {ch && (
              <span style={{ fontSize: 13, color: C.grey }}>🏗 {nomDuChantier(ch, chantiers)}</span>
            )}
            <span style={{ fontSize: 12.5, color: C.grey }}>
              {(x.lignes || [])
                .filter((l) => String(l.d || '').trim())
                .slice(0, 4)
                .map((l) => l.d + ' ' + quantite(l))
                .join(' · ')}
            </span>
          </button>
        );
      })}
    </Shell>
  );
}
